/** The offer card (prototype offerCard): one pending request, from either side. Also the engagement overlay: offers sent to the person sit in the middle of the screen. A posted rider is only a row in the driver's list. */
import type { OfferView } from '@konkride/shared';
import { PN, esc, money, npl, rgB } from './util';
import { gmRoute, vehCard } from './views-common';
import type { Snap } from './types';

/** My pending offers, oldest first: the ones sent to me (they need my answer) and the ones I sent (I wait). */
export function myOffers(s: Snap): { incoming: OfferView[]; waiting: OfferView[] } {
  const me = s.user!.id;
  const all = s.live.offers.filter((o) => o.status === 'pending' && (o.to.id === me || o.from.id === me)).sort((a, b) => a.createdAt - b.createdAt).slice(0, 5);
  return { incoming: all.filter((o) => o.to.id === me), waiting: all.filter((o) => o.to.id !== me) };
}
/** Offers waiting for my answer, while I am online on the side I am looking at. */
const offersToAnswer = (s: Snap): OfferView[] => ((s.ui.view === 'rider' ? s.rp.online : s.dp.online) ? myOffers(s).incoming : []);
/** True while an offer sent to the person waits for their answer: the beep and the centre card use this one rule. A posted rider (Name your fare, Local) is only a row in the driver's list: no beep, no card. */
export const needsAnswer = (s: Snap): boolean => !!s.user && !s.live.trip && offersToAnswer(s).length > 0;

/** The centre-of-screen cards: offers sent to the person, which need an answer now. Empty when there are none. */
export function engagement(s: Snap): string {
  const cards = needsAnswer(s) ? offersToAnswer(s) : [];
  if (!cards.length) return '';
  const role = s.ui.view === 'rider' ? 'r' : 'd';
  return `<div class="engage" role="alert"><div>${cards.map((o) => offerCard(s, o, role)).join('')}</div></div>`;
}

export function offerCard(s: Snap, o: OfferView, role: 'd' | 'r'): string {
  const me = s.user!.id;
  const incoming = o.to.id === me;
  const other = incoming ? o.from : o.to;
  const name = incoming ? o.fromName : o.toName;
  const rider = s.live.riders.find((x) => x.id === o.from.id || x.id === o.to.id);
  const veh = role === 'r' ? s.veh(other.id) : undefined;
  const who = role === 'd' ? `${esc(name)} · ${o.distKm.toFixed(1)} km away` : `${esc(name)}${veh?.plate ? ' · ' + npl(veh.plate) : ''}`;
  const local = o.rg === 'local';
  const flash = incoming;
  const secs = Math.max(0, Math.ceil((o.expiresAt - s.now) / 1000));
  const route = role === 'd'
    ? gmRoute(s, { k: 'r', pos: rider?.pos ?? null, dn: o.destName || '' }) + (local ? '' : `<span class="mute">Passengers: ${rider?.pax ?? 1}</span>`)
    : '';
  return `<div class="card big${flash ? ' flash' : ''}"><div class="row" style="align-items:center"><b>${who}</b><span class="mute nw">${o.kind === 'auto' ? 'Auto match' : 'Request'} · <span data-exp="${o.expiresAt}">${secs}</span>s</span></div>${rgB(local)}<div class="offer"><span>${PN[o.src] || 'Price'} to ${esc(o.destName || 'Destination')}</span><span class="v">${money(o.fare)}</span></div>${route}${role === 'r' ? vehCard(s, other.id, local) : ''}${incoming
    ? `<div class="row"><button class="btn out" data-act="odec" data-id="${esc(o.id)}">Cancel</button><button class="btn" style="flex:2" data-act="oacc" data-id="${esc(o.id)}">Accept</button></div>`
    : `<p class="mute">Waiting for the ${role === 'r' ? 'driver' : 'rider'} to accept…</p><button class="btn out" data-act="odec" data-id="${esc(o.id)}">Cancel</button>`}</div>`;
}
